import type { Result, AppError } from '../../error/types';
import type { EnvironmentValue } from '../types/EnvironmentValue';
import { ok, err } from '../../error/functions';

/**
 * @group Functions
 * @summary <blockquote>Gets environment variable values with proper error handling using the Result pattern.</blockquote>
 * 
 * @param envKey - The environment variable key to retrieve (must be a valid EnvironmentValue)
 * @param required - Whether the environment variable is required (defaults to false)
 * @returns Result containing the environment variable value or an AppError if not found/required
 * 
 * @example
 * ```typescript
 * import { getEnvironmentValue, ENVIRONMENT_VALUES } from '@shared/utils';
 * 
 * const result = getEnvironmentValue(ENVIRONMENT_VALUES.DATABASE.POSTGRES_URL, true);
 * result.match({
 *   ok: (value) => console.log(`Database URL: ${value}`),
 *   err: (error) => console.error(`Missing: ${error.errorMessage}`)
 * });
 * ```
 */
export function getEnvironmentValue(
  envKey: EnvironmentValue, 
  required: boolean = false
): Result<string, AppError> {
  // In React Native with Metro bundler, process.env[variable] doesn't work
  // because Metro only inlines static property access like process.env.VAR_NAME
  // We use a switch statement to enable static access for all environment variables
  let value: string | undefined;
  
  // Static property access for Metro bundler compatibility (React Native)
  // Metro can inline these at build time because they're static property accesses
  switch (envKey) {
    // URL variables - Mobile
    case 'EXPO_PUBLIC_API_URL':
      value = process.env.EXPO_PUBLIC_API_URL;
      break;
    case 'EXPO_PUBLIC_PREVIEW_URL':
      value = process.env.EXPO_PUBLIC_PREVIEW_URL;
      break;
    case 'EXPO_PUBLIC_DOXY_URL':
      value = process.env.EXPO_PUBLIC_DOXY_URL;
      break;
    case 'VERCEL_URL':
      value = process.env.VERCEL_URL;
      break;
    case 'PRODUCTION_API_URL':
      value = process.env.PRODUCTION_API_URL;
      break;
    case 'MOBILE_PREVIEW_FALLBACK_URL':
      value = process.env.MOBILE_PREVIEW_FALLBACK_URL;
      break;
    case 'MOBILE_PRODUCTION_FALLBACK_URL':
      value = process.env.MOBILE_PRODUCTION_FALLBACK_URL;
      break;
    case 'DEFAULT_PREVIEW_URL':
      value = process.env.DEFAULT_PREVIEW_URL;
      break;
    case 'DEFAULT_PRODUCTION_URL':
      value = process.env.DEFAULT_PRODUCTION_URL;
      break;
    case 'DEFAULT_DEVELOPMENT_URL':
      value = process.env.DEFAULT_DEVELOPMENT_URL;
      break;
    // URL variables - Web
    case 'STAGING_API_URL':
      value = process.env.STAGING_API_URL;
      break;
    case 'NEXT_PUBLIC_DOXY_URL':
      value = process.env.NEXT_PUBLIC_DOXY_URL;
      break;
    case 'RESEND_URL':
      value = process.env.RESEND_URL;
      break;
    case 'AXIOM_URL':
      value = process.env.AXIOM_URL;
      break;
    case 'VERCEL_PROJECT_PRODUCTION_URL':
      value = process.env.VERCEL_PROJECT_PRODUCTION_URL;
      break;
    case 'NEXT_PUBLIC_APP_URL':
      value = process.env.NEXT_PUBLIC_APP_URL;
      break;
    case 'NEXT_PUBLIC_URL':
      value = process.env.NEXT_PUBLIC_URL;
      break;
    case 'EXPO_PUBLIC_ANDROID_DOWNLOAD_URL':
      value = process.env.EXPO_PUBLIC_ANDROID_DOWNLOAD_URL;
      break;
    case 'EXPO_PUBLIC_VERSION_GATE_POLICY_URL':
      value = process.env.EXPO_PUBLIC_VERSION_GATE_POLICY_URL;
      break;
    // Environment detection
    case 'NEXT_PUBLIC_VERCEL_ENV':
      value = process.env.NEXT_PUBLIC_VERCEL_ENV;
      break;
    case 'NEXT_PUBLIC_NODE_ENV':
      value = process.env.NEXT_PUBLIC_NODE_ENV;
      break;
    // Database
    case 'POSTGRES_URL':
      value = process.env.POSTGRES_URL;
      break;
    case 'LOCAL_POSTGRES_URL':
      value = process.env.LOCAL_POSTGRES_URL;
      break;
    case 'OVERRIDE_POSTGRES_URL':
      value = process.env.OVERRIDE_POSTGRES_URL;
      break;
    // Auth
    case 'NEXT_PUBLIC_AUTH_SECRET':
      value = process.env.NEXT_PUBLIC_AUTH_SECRET;
      break;
    case 'AUTH_JWT_EXPIRY':
      value = process.env.AUTH_JWT_EXPIRY;
      break;
    case 'AUTH_SESSION_MAX_AGE':
      value = process.env.AUTH_SESSION_MAX_AGE;
      break;
    // Analytics
    case 'NEXT_PUBLIC_POSTHOG_KEY':
      value = process.env.NEXT_PUBLIC_POSTHOG_KEY;
      break;
    case 'NEXT_PUBLIC_POSTHOG_HOST':
      value = process.env.NEXT_PUBLIC_POSTHOG_HOST;
      break;
    case 'EXPO_PUBLIC_POSTHOG_KEY':
      value = process.env.EXPO_PUBLIC_POSTHOG_KEY;
      break;
    case 'EXPO_PUBLIC_POSTHOG_HOST':
      value = process.env.EXPO_PUBLIC_POSTHOG_HOST;
      break;
    // Logging
    case 'LOG_LEVEL':
      value = process.env.LOG_LEVEL;
      break;
    case 'DEBUG':
      value = process.env.DEBUG;
      break;
    case 'DEBUG_AUTH':
      value = process.env.DEBUG_AUTH;
      break;
    case 'AXIOM_DATASET':
      value = process.env.AXIOM_DATASET;
      break;
    case 'AXIOM_TOKEN':
      value = process.env.AXIOM_TOKEN;
      break;
    // Sentry
    case 'SENTRY_DSN':
      value = process.env.SENTRY_DSN;
      break;
    case 'NEXT_PUBLIC_SENTRY_DSN':
      value = process.env.NEXT_PUBLIC_SENTRY_DSN;
      break;
    case 'EXPO_PUBLIC_SENTRY_DSN':
      value = process.env.EXPO_PUBLIC_SENTRY_DSN;
      break;
    // Testing
    case 'JEST_WORKER_ID':
      value = process.env.JEST_WORKER_ID;
      break;
    // Email
    case 'RESEND_API_KEY':
      value = process.env.RESEND_API_KEY;
      break;
    case 'EMAIL_FROM':
      value = process.env.EMAIL_FROM;
      break;
    case 'EMAIL_REPLY_TO':
      value = process.env.EMAIL_REPLY_TO;
      break;
    // Build
    case 'NEXT_PHASE':
      value = process.env.NEXT_PHASE;
      break;
    case 'RAILWAY_ENVIRONMENT':
      value = process.env.RAILWAY_ENVIRONMENT;
      break;
    // Vercel
    case 'VERCEL_ENV':
      value = process.env.VERCEL_ENV;
      break;
    case 'VERCEL_GIT_COMMIT_REF':
      value = process.env.VERCEL_GIT_COMMIT_REF;
      break;
    case 'VERCEL_GIT_COMMIT_SHA':
      value = process.env.VERCEL_GIT_COMMIT_SHA;
      break;
    case 'VERCEL_GIT_PULL_REQUEST_ID':
      value = process.env.VERCEL_GIT_PULL_REQUEST_ID;
      break;
    case 'SHENAI_API_KEY':
      value = process.env.SHENAI_API_KEY;
      break;
    case 'SHENAI_ADMIN_CLIENT_ID':
      value = process.env.SHENAI_ADMIN_CLIENT_ID;
      break;
    case 'SHENAI_ADMIN_CLIENT_SECRET':
      value = process.env.SHENAI_ADMIN_CLIENT_SECRET;
      break;
    // OpenEMR
    case 'OPENEMR_BASE_URL':
      value = process.env.OPENEMR_BASE_URL;
      break;
    case 'OPENEMR_CLIENT_ID':
      value = process.env.OPENEMR_CLIENT_ID;
      break;
    case 'OPENEMR_CLIENT_SECRET':
      value = process.env.OPENEMR_CLIENT_SECRET;
      break;
    case 'OPENEMR_USERNAME':
      value = process.env.OPENEMR_USERNAME;
      break;
    case 'OPENEMR_PASSWORD':
      value = process.env.OPENEMR_PASSWORD;
      break;
    case 'OPENEMR_REFRESH_TOKEN':
      value = process.env.OPENEMR_REFRESH_TOKEN;
      break;
    // Payments
    case 'PAYSTACK_SECRET_KEY':
      value = process.env.PAYSTACK_SECRET_KEY;
      break;
    case 'PAYSTACK_PUBLIC_KEY':
      value = process.env.PAYSTACK_PUBLIC_KEY;
      break;
    // GitHub
    case 'GITHUB_TOKEN':
      value = process.env.GITHUB_TOKEN;
      break;
    case 'GITHUB_REPOSITORY_OWNER':
      value = process.env.GITHUB_REPOSITORY_OWNER;
      break;
    case 'GITHUB_REPOSITORY_NAME':
      value = process.env.GITHUB_REPOSITORY_NAME;
      break;
    case 'GITHUB_ACTIONS':
      value = process.env.GITHUB_ACTIONS;
      break;
    // External services
    case 'AWS_VERSION_GATE_S3_BUCKET':
      value = process.env.AWS_VERSION_GATE_S3_BUCKET;
      break;
    case 'AWS_VERSION_GATE_S3_KEY':
      value = process.env.AWS_VERSION_GATE_S3_KEY;
      break;
    // Feature flags
    case 'VERSION_GATE_FAILURE_MODE':
      value = process.env.VERSION_GATE_FAILURE_MODE;
      break;
    case 'VERSION_GATE_SIGNIN_ENFORCEMENT':
      value = process.env.VERSION_GATE_SIGNIN_ENFORCEMENT;
      break;
    // Testing
    case '.env.test.local':
      value = process.env['.env.test.local'];
      break;
    // Scanner
    case 'SCANNER_API_KEY':
      value = process.env.SCANNER_API_KEY;
      break;
    case 'SCANNER_API_URL':
      value = process.env.SCANNER_API_URL;
      break;
    // Default: try dynamic access (works in Node.js but not React Native)
    default:
      value = process.env[envKey];
      break;
  }
  
  if (!value) {
    if (required) {
      return err({
        errorCode: 'MISSING_REQUIRED_ENV_VAR',
        errorMessage: `Required environment variable '${envKey}' is not set`
      });
    }
    
    return err({
      errorCode: 'MISSING_ENV_VAR',
      errorMessage: `Environment variable '${envKey}' is not set`
    });
  }
  
  return ok(value);
} 